'use client';

import { useRunnerRequestUiRuntime, type RunnerRequestUiRuntime } from '../runtime';

import { useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { toast } from 'sonner';
import { maxAttachmentsPerDaemonTrigger, type PendingAttachmentDraft } from '../types/attachment';

type UseAttachmentDraftUploadOptions = {
  drafts: PendingAttachmentDraft[];
  onChange: (drafts: PendingAttachmentDraft[]) => void;
  max?: number;
  disabled?: boolean;
};

export const useAttachmentDraftUploadWithRuntime = (
  { drafts, onChange, max = maxAttachmentsPerDaemonTrigger, disabled = false }: UseAttachmentDraftUploadOptions,
  runtime: RunnerRequestUiRuntime,
) => {
  const { useWorkspaceScope, validateAttachmentFile, useUploadDraftAttachment } = runtime;
  const t = useTranslations('components.appLayout');
  const { token } = useWorkspaceScope();
  const uploadMutation = useUploadDraftAttachment(token);
  const [uploadingCount, setUploadingCount] = useState(0);
  const isUploadingRef = useRef(false);
  const count = drafts.length;
  const canAdd = !disabled && uploadingCount === 0 && count < max;

  const addFiles = async (incoming: FileList | File[]) => {
    if (disabled || isUploadingRef.current) return;

    const nextFiles = Array.from(incoming);
    if (nextFiles.length === 0) return;
    if (count + nextFiles.length > max) {
      toast.error(t('attachmentMaxExceeded', { max }));
      return;
    }
    try {
      nextFiles.forEach((file) => validateAttachmentFile(file));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t('attachmentUnsupportedFile'));
      return;
    }

    isUploadingRef.current = true;
    setUploadingCount(nextFiles.length);
    const accumulated: PendingAttachmentDraft[] = [];
    try {
      for (const file of nextFiles) {
        const draft = await uploadMutation.mutateAsync({ file });
        accumulated.push(draft);
        setUploadingCount((current) => current - 1);
      }
      onChange([...drafts, ...accumulated]);
      setUploadingCount(0);
      isUploadingRef.current = false;
    } catch (error) {
      // 부분 업로드된 항목은 lifecycle 정책(drafts/ 24h)으로 자동 정리됨
      toast.error(error instanceof Error ? error.message : t('attachmentUploadFailed'));
      setUploadingCount(0);
      isUploadingRef.current = false;
      if (accumulated.length > 0) {
        onChange([...drafts, ...accumulated]);
      }
    }
  };

  return {
    addFiles,
    canAdd,
    count,
    max,
    uploadingCount,
  };
};

export const useAttachmentDraftUpload = (options: UseAttachmentDraftUploadOptions) => {
  const runtime = useRunnerRequestUiRuntime();
  return useAttachmentDraftUploadWithRuntime(options, runtime);
};
